import { GaussianSplatCapabilityProvider } from './gaussian-splat-capability-provider';
import { isViewableSplat, SplatsApiService } from './splats-api-service';
import { resolveSplatLoadTarget } from './resolve-splat-load-target';
import { GaussianSplatRef } from './load-gaussian-splat.types';

export async function loadGaussianSplat(
  item: GaussianSplatRef,
  splatsApi: SplatsApiService,
  tenantTrn: string,
  provider: GaussianSplatCapabilityProvider,
  reload: boolean,
  signal?: AbortSignal
): Promise<void> {
  if (reload) {
    await provider.unloadTileset(item.id);
  }

  const splat = await splatsApi.getSplat(
    tenantTrn,
    item.datasetId,
    item.id,
    signal
  );

  if (!isViewableSplat(splat)) {
    throw new Error(
      `Gaussian splat ${item.id} is not ready for viewing (status=${splat.status ?? 'unknown'})`
    );
  }

  signal?.throwIfAborted();

  const downloadUrl = splat.downloadUrl;
  if (downloadUrl != null && downloadUrl.length > 0) {
    const target = await resolveSplatLoadTarget(
      {
        downloadUrl,
        tilesetUrl: splat.tilesetUrl,
        path: splat.path,
      },
      signal
    );

    if (target.kind === 'ply') {
      assertModelPresent(
        await provider.loadPly({
          id: item.id,
          url: target.url,
          fitToView: false,
        }),
        item.id
      );
      return;
    }

    assertModelPresent(
      await provider.loadTileset({
        id: item.id,
        tilesetUrl: target.tilesetUrl,
        templateUrl: target.templateUrl,
        fitToView: false,
      }),
      item.id
    );
    return;
  }

  const tilesetUrl = splat.tilesetUrl;
  if (tilesetUrl == null || tilesetUrl.length === 0) {
    throw new Error(
      `Gaussian splat ${item.id} is not ready for viewing (missing downloadUrl)`
    );
  }

  assertModelPresent(
    await provider.loadTileset({
      id: item.id,
      tilesetUrl,
      templateUrl: tilesetUrl,
      fitToView: false,
    }),
    item.id
  );
}

function assertModelPresent(
  model: object | null,
  itemId: string
): asserts model is object {
  if (model == null) {
    // loadPly / loadTileset resolve null when cleanup() runs mid-load or a
    // duplicate load cannot start. That is a cancellation, not a failed load.
    const error = new Error(
      `Gaussian splat ${itemId} was not added to the viewer`
    );
    error.name = 'AbortError';
    throw error;
  }
}
